/*
* Research bids (order search) for authenticated users
*/

import { Grid } from '@progress/kendo-grid-react-wrapper';
import '@progress/kendo-ui';
import * as _ from 'lodash';
import { Range, createSliderWithTooltip } from 'rc-slider';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import VirtualizedSelect from 'react-virtualized-select';
import { compose } from 'redux';
import { connectRequest, updateEntities } from 'redux-query';
import * as JSZip from 'jszip';
import fileDownload from 'js-file-download';
import moment from 'moment';

import { Cancelable } from 'lodash';
import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { DateRanges } from '../common/dateranges';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import { OrderPricing } from '../order/orderpricing';
import * as ReduxQueries from '../queries';
import { ApiQueryResponse } from '../queries/apiqueryresponse';
import * as LK from '../config/apilookupkeys';
import SearchInput from 'src/common/searchInput';
import * as MutateActions from '../actions/mutate';

(window as any).JSZip = JSZip;

export interface IResearchBidsProps extends AppComp.IAppComponentProps {
    filterByAssociationTypes?: any;
    filterByClients?: any;
    filterByRegions?: any;
    filterByStatuses?: any;
    filterByStatusesMulti?: any;
    filterByStreetTypes?: any;
    filterByAssociationTypesMulti?: any;
    filterByClientsMulti?: any;
    filterByRegionsMulti?: any;
    filterByStreetTypesMulti?: any;
    downloadKML?: any;
}

const RangeWithTooltip = createSliderWithTooltip(Range);

class ResearchBids extends AppComp.AppComponent<IResearchBidsProps, any> {
    public readonly anyOptionText: string = this.appTextFilterOptionAny();
    public readonly anyOptionValueStatusId: number = -1;
    public readonly anyOptionValueTextValue: string = '';

    public debouncedRangeSearch: ((low: number, high: number, action: any) => void) & Cancelable = _.debounce((low, high, action) => { this.props.dispatch(action(low, high)); }, 400);

    // Server-side pagination properties
    private gridDataSource: any = null;
    private gridWidget: any = null;
    private lastFiltersHash: string = '';

    public rangeConfigUnits = {
        defaultValue: [this.props.search.filters.unitsLow, this.props.search.filters.unitsHigh],
        min: 0,
        max: 25000,
        marks: {
            0: '0',
            5000: '5000',
            10000: '10000',
            15000: '15000',
            20000: '20000',
            25000: '25000',
        },
        tipFormatter: (v) => `${v} ${this.appText('units')}`
    };

    public rangeConfigBuildings = {
        defaultValue: [this.props.search.filters.buildingsLow, this.props.search.filters.buildingsHigh],
        min: 0,
        max: 5000,
        marks: {
            0: '0',
            1250: '1250',
            2500: '2500',
            3750: '3750',
            5000: '5000',
        },
        tipFormatter: (v) => `${v} ${this.appText('buildings')}`
    };

    public minPoolSpaValue: number = 0;
    public maxPoolSpaValue: number = 100;

    public defHid1 = false;
    public defHid2 = true;
    public defHid3 = true;
    public defHid4 = true;

    public linkColumnTemplate = GridUtilities.kendoGridLinkColumnTemplate('viewOrderDetailLink', 'viewOrderDetailLinkHref', this.appText('view-order-detail-link-title'));

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 50, 200, undefined, { hidden: this.defHid1 }),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 180, 220, undefined, { hidden: this.defHid1 }),
            GridUtilities.kendoGridColumnDefinition('clientName', this.appTextGridColumnHeader('client-name'), 180, 240, undefined, { hidden: this.defHid1 }),

            // Hidden columns for KML export
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapAddress1', 1003),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapCity', 1004),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapState', 1005),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapZip', 1006),
            GridUtilities.kendoGridColumnDefinition('clientAddress', this.appTextGridColumnHeader('client-street-address'), 40, 242, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('clientCity', this.appTextGridColumnHeader('client-city'), 40, 243, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('clientState', this.appTextGridColumnHeader('client-state'), 40, 244, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('clientPostal', this.appTextGridColumnHeader('client-postal-code'), 40, 245, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('regionName', this.appTextGridColumnHeader('region-name'), 90, 260, undefined, { hidden: this.defHid1 }),
            GridUtilities.kendoGridColumnDefinition('bidDateValue', this.appTextGridColumnHeader('bid-date'), 70, 280, undefined, { format: '{0:d}' }),
            GridUtilities.kendoGridColumnDefinition('orderStatus', this.appTextGridColumnHeader('order-status'), 100, 300, undefined, { hidden: this.defHid1 }),
            GridUtilities.kendoGridColumnDefinition('siDueDate', this.appTextGridColumnHeader('site-inspection-date'), 60, 320, undefined, { hidden: this.defHid1, format: '{0:d}' }),
            GridUtilities.kendoGridColumnDefinition('completionDateValue', this.appTextGridColumnHeader('completion-date'), 60, 321, undefined, { format: '{0:d}' }),
            GridUtilities.kendoGridColumnDefinition('level1Economy', this.appTextGridColumnHeader('level-1-economy'), 90, 340, undefined, { format: '{0:c}' }),
            GridUtilities.kendoGridColumnDefinition('level2Economy', this.appTextGridColumnHeader('level-2-economy'), 90, 341, undefined, { format: '{0:c}' }),
            GridUtilities.kendoGridColumnDefinition('level3Economy', this.appTextGridColumnHeader('level-3-economy'), 90, 342, undefined, { format: '{0:c}' }),
            GridUtilities.kendoGridColumnDefinition('feedback', this.appTextGridColumnHeader('feedback'), 80, 360, undefined, { hidden: this.defHid1 }),

            GridUtilities.kendoGridColumnDefinition('acceptPrice', this.appTextGridColumnHeader('accept-price'), 50, 380, undefined, { hidden: this.defHid2, format: '{0:c}' }),
            GridUtilities.kendoGridColumnDefinition('managerUser', this.appTextGridColumnHeader('manager'), 180, 520, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('coordinatorUser', this.appTextGridColumnHeader('coordinator'), 180, 520, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('associationCity', this.appTextGridColumnHeader('association-city'), 80, 400, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('associationState', this.appTextGridColumnHeader('association-state'), 40, 420, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('associationMailingAddress', this.appTextGridColumnHeader('association-mailing-address'), 40, 421, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('associationType', this.appTextGridColumnHeader('association-type'), 90, 440, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('numberBuildings', this.appTextGridColumnHeader('buildings'), 40, 460, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('recRooms', this.appTextGridColumnHeader('clubhouse'), 40, 480, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('constructDate', this.appTextGridColumnHeader('construct-date'), 75, 500, undefined, { hidden: this.defHid2 }),
            GridUtilities.kendoGridColumnDefinition('preparedForFYEDate', this.appTextGridColumnHeader('prepared-for-fye-date'), 75, 501, undefined, { hidden: this.defHid2 }),

            GridUtilities.kendoGridColumnDefinition('contactName', this.appTextGridColumnHeader('contact'), 180, 520, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('contactEmail', this.appTextGridColumnHeader('contact-email'), 180, 521, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('elevators', this.appTextGridColumnHeader('elevators'), 40, 540, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('fencing', this.appTextGridColumnHeader('fencing'), 40, 560, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('gated', this.appTextGridColumnHeader('gated'), 40, 580, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('interiorHalls', this.appTextGridColumnHeader('int-halls'), 40, 600, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('irrigation', this.appTextGridColumnHeader('irrigation'), 40, 620, undefined, { hidden: this.defHid3 }),
            GridUtilities.kendoGridColumnDefinition('mechanical', this.appTextGridColumnHeader('mechanical'), 40, 640, undefined, { hidden: this.defHid3 }),

            GridUtilities.kendoGridColumnDefinition('siding', this.appTextGridColumnHeader('painted-exterior'), 50, 660, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('pools', this.appTextGridColumnHeader('pools'), 40, 680, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('roofs', this.appTextGridColumnHeader('roofs'), 40, 700, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('service', this.appTextGridColumnHeader('order-service'), 70, 720, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('spas', this.appTextGridColumnHeader('spas'), 40, 740, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('numberStories', this.appTextGridColumnHeader('stories'), 40, 760, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('streets', this.appTextGridColumnHeader('streets'), 50, 780, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('tennis', this.appTextGridColumnHeader('tennis'), 40, 800, undefined, { hidden: this.defHid4 }),
            GridUtilities.kendoGridColumnDefinition('numberUnits', this.appTextGridColumnHeader('units'), 40, 820, undefined, { hidden: this.defHid4 }),

            GridUtilities.kendoGridColumnDefinition('viewOrderDetailLink', '', 80, 840, this.linkColumnTemplate, { headerTemplate: '' }),
        ],
        this.appText('no-results'),
        {
            groupable: true,
            columnMenu: true,
            scrollable: {
                virtual: false, // Disable virtual scrolling for server-side paging
            },
            pageable: {
                refresh: true, // Enable refresh button
            },
            saveChanges: (e) => { this.handleExportKML(e); },
            editable: true,
            toolbar: [
                'excel',
                {
                    name: 'save',
                    text: 'Export to Google Earth',
                    iconClass: 'k-icon k-i-globe'
                },
                'pdf',
            ]
        },
        this.appTextPageTitle()
    );

    public constructor(props: IResearchBidsProps) {
        super(props, 'app.research-bids.');
        this.state = {
            unitsMin: 0,
            unitsMax: 10000,
            buildingsMin: 0,
            buildingsMax: 5000,
            searchTriggered: false, // Only call API when search button is clicked
            searchVersion: 0, // Increment to trigger new search
            appliedFilters: null, // Cache filters when Search is clicked, used for pagination
            exportingKML: false, // True while fetching all data and exporting to Google Earth
            showModalConfirmExport: false, // Show confirmation modal before exporting
            // Infinite scroll state
            infiniteScrollData: [], // Accumulated data for "All" view
            infiniteScrollLoading: false, // True while loading more batches
            infiniteScrollTotal: 0, // Total count from first request
            infiniteScrollCurrentPage: 0, // Current page being loaded
            infiniteScrollHasMore: false, // Whether more data is available
            // Toast notifications
            showInfiniteScrollToast: false, // Show toast when infinite scroll starts
            showExportToast: false, // Show toast when export starts
            // Export progress
            exportProgress: {
                current: 0,
                total: 0,
            },
        };
    }

    public componentDidMount() {
        // Initialize filters hash - dataSource will be created in render()
        this.lastFiltersHash = this.getFiltersHash();
        // Load filter options for dropdowns on initial page load
        this.loadFilterOptions();
    }

    private loadFilterOptions() {
        const filtersApiUrl = ApiEndpoints.getResearchBidsFiltersUrl();
        const accessToken = ReduxQueries.PropsAccessToken(this.props);

        fetch(filtersApiUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                const apiResponse = new ApiQueryResponse(data);

                // Update filter options in Redux entities (for dropdown menus)
                // Backend returns: clients, regions, statuses, associationTypes, streetTypes
                this.props.dispatch(updateEntities({
                    researchBidsAssociationTypes: () => apiResponse.getFilterAsArray(LK.LookupKeys.AssociationTypes, true),
                    researchBidsClients: () => apiResponse.getFilterAsArray(LK.LookupKeys.Clients, true),
                    researchBidsRegions: () => apiResponse.getFilterAsArray(LK.LookupKeys.Regions, true),
                    researchBidsStatuses: () => apiResponse.getFilterAsArray(LK.LookupKeys.Statuses, true),
                    researchBidsStreetTypes: () => apiResponse.getFilterAsArray(LK.LookupKeys.StreetTypes, true),
                    researchBidsAssociationTypesMulti: () => apiResponse.getFilterAsArray(LK.LookupKeys.AssociationTypes, true),
                    researchBidsClientsMulti: () => apiResponse.getFilterAsArray(LK.LookupKeys.Clients, true),
                    researchBidsRegionsMulti: () => apiResponse.getFilterAsArray(LK.LookupKeys.Regions, true),
                    researchBidsStatusesMulti: () => apiResponse.getFilterAsArray(LK.LookupKeys.Statuses, true),
                    researchBidsStreetTypesMulti: () => apiResponse.getFilterAsArray(LK.LookupKeys.StreetTypes, true),
                }));
            })
            .catch((error) => {
                console.error('Error loading filter options:', error);
            });
    }

    public componentWillUnmount() {
        if (this.gridDataSource && typeof this.gridDataSource.destroy === 'function') {
            this.gridDataSource.destroy();
        }
        this.gridDataSource = null;
    }

    private getFiltersHash(): string {
        // Only include searchVersion in hash - DataSource only recreated when Search button is clicked
        // Filter values are read from state/props in readFunction, not from hash
        return JSON.stringify({
            searchVersion: this.state.searchVersion,
        });
    }

    /** Page size for Google export "infinite scroll" fetching (same filters as grid). */
    private static readonly EXPORT_PAGE_SIZE = 500;
    /** Page size for "All" results batching in grid (same as export). */
    private static readonly GRID_ALL_PAGE_SIZE = 500;

    /**
     * Build query params for search/export (same filters as grid search). Caller adds page/limit.
     */
    private buildSearchQueryParams(): URLSearchParams {
        const filters = this.props.search.filters;
        const applied = this.state.appliedFilters || {};
        const queryParams = new URLSearchParams();
        queryParams.append('associationTypeId', Formatters.int(filters.associationTypeId).toString());
        queryParams.append('buildingsHigh', Formatters.int(applied.buildingsMax || filters.buildingsHigh).toString());
        queryParams.append('buildingsLow', Formatters.int(applied.buildingsMin || filters.buildingsLow).toString());
        queryParams.append('clientId', Formatters.int(filters.clientId).toString());
        queryParams.append('contactKeyword', applied.contactKeyword || filters.contactKeyword || '');
        queryParams.append('fromBidDate', applied.fromBidDate || filters.fromBidDate || '');
        queryParams.append('toBidDate', applied.toBidDate || filters.toBidDate || '');
        queryParams.append('fromCompletionDate', applied.fromCompletionDate || filters.fromCompletionDate || '');
        queryParams.append('toCompletionDate', applied.toCompletionDate || filters.toCompletionDate || '');
        queryParams.append('pools', Formatters.int(filters.pools).toString());
        queryParams.append('regionId', Formatters.int(filters.regionId).toString());
        queryParams.append('spas', Formatters.int(filters.spas).toString());
        queryParams.append('statusId', Formatters.int(filters.statusId).toString());
        queryParams.append('streets', filters.streets || '');
        queryParams.append('unitsHigh', Formatters.int(applied.unitsMax || filters.unitsHigh).toString());
        queryParams.append('unitsLow', Formatters.int(applied.unitsMin || filters.unitsLow).toString());
        queryParams.append('operator', filters.operator || 'AND');
        const associationTypeIdsMulti = applied.associationTypeIdsMulti || filters.associationTypeIdsMulti || [-1];
        const clientIdsMulti = applied.clientIdsMulti || filters.clientIdsMulti || [-1];
        const poolsMulti = applied.poolsMulti || filters.poolsMulti || [-1];
        const regionIdsMulti = applied.regionIdsMulti || filters.regionIdsMulti || [-1];
        const spasMulti = applied.spasMulti || filters.spasMulti || [-1];
        const statusIdsMulti = applied.statusIdsMulti || filters.statusIdsMulti || [-1];
        const streetsMulti = applied.streetsMulti || filters.streetsMulti || [''];
        queryParams.append('associationTypeIdsMulti', associationTypeIdsMulti.join(','));
        queryParams.append('clientIdsMulti', clientIdsMulti.join(','));
        queryParams.append('poolsMulti', poolsMulti.join(','));
        queryParams.append('regionIdsMulti', regionIdsMulti.join(','));
        queryParams.append('spasMulti', spasMulti.join(','));
        queryParams.append('statusIdsMulti', statusIdsMulti.join(','));
        queryParams.append('streetsMulti', streetsMulti.join(','));
        return queryParams;
    }

    /**
     * Build query params for export (same filters as grid search). Caller adds page/limit.
     * @deprecated Use buildSearchQueryParams() instead
     */
    private buildExportQueryParams(): URLSearchParams {
        const filters = this.props.search.filters;
        const applied = this.state.appliedFilters || {};
        const queryParams = new URLSearchParams();
        queryParams.append('associationTypeId', Formatters.int(filters.associationTypeId).toString());
        queryParams.append('buildingsHigh', Formatters.int(applied.buildingsMax || filters.buildingsHigh).toString());
        queryParams.append('buildingsLow', Formatters.int(applied.buildingsMin || filters.buildingsLow).toString());
        queryParams.append('clientId', Formatters.int(filters.clientId).toString());
        queryParams.append('contactKeyword', applied.contactKeyword || filters.contactKeyword || '');
        queryParams.append('fromBidDate', applied.fromBidDate || filters.fromBidDate || '');
        queryParams.append('toBidDate', applied.toBidDate || filters.toBidDate || '');
        queryParams.append('fromCompletionDate', applied.fromCompletionDate || filters.fromCompletionDate || '');
        queryParams.append('toCompletionDate', applied.toCompletionDate || filters.toCompletionDate || '');
        queryParams.append('pools', Formatters.int(filters.pools).toString());
        queryParams.append('regionId', Formatters.int(filters.regionId).toString());
        queryParams.append('spas', Formatters.int(filters.spas).toString());
        queryParams.append('statusId', Formatters.int(filters.statusId).toString());
        queryParams.append('streets', filters.streets || '');
        queryParams.append('unitsHigh', Formatters.int(applied.unitsMax || filters.unitsHigh).toString());
        queryParams.append('unitsLow', Formatters.int(applied.unitsMin || filters.unitsLow).toString());
        queryParams.append('operator', filters.operator || 'AND');
        const associationTypeIdsMulti = applied.associationTypeIdsMulti || filters.associationTypeIdsMulti || [-1];
        const clientIdsMulti = applied.clientIdsMulti || filters.clientIdsMulti || [-1];
        const poolsMulti = applied.poolsMulti || filters.poolsMulti || [-1];
        const regionIdsMulti = applied.regionIdsMulti || filters.regionIdsMulti || [-1];
        const spasMulti = applied.spasMulti || filters.spasMulti || [-1];
        const statusIdsMulti = applied.statusIdsMulti || filters.statusIdsMulti || [-1];
        const streetsMulti = applied.streetsMulti || filters.streetsMulti || [''];
        queryParams.append('associationTypeIdsMulti', associationTypeIdsMulti.join(','));
        queryParams.append('clientIdsMulti', clientIdsMulti.join(','));
        queryParams.append('poolsMulti', poolsMulti.join(','));
        queryParams.append('regionIdsMulti', regionIdsMulti.join(','));
        queryParams.append('spasMulti', spasMulti.join(','));
        queryParams.append('statusIdsMulti', statusIdsMulti.join(','));
        queryParams.append('streetsMulti', streetsMulti.join(','));
        return queryParams;
    }

    /**
     * Fetch one page of research bids (for regular search or export).
     * @param page Page number (1-based)
     * @param limit Number of records per page
     * @param useExportApi If true, use export-all API; otherwise use regular search API
     * @returns Promise resolving to { results: any[], total: number }
     */
    private fetchResearchBidsPage(page: number, limit: number, useExportApi: boolean = false): Promise<{ results: any[], total: number }> {
        const apiUrl = useExportApi 
            ? ApiEndpoints.getResearchBidsExportAllUrl()
            : getApiUrl();
        const accessToken = ReduxQueries.PropsAccessToken(this.props);
        const queryParams = this.buildSearchQueryParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        const url = `${apiUrl}?${queryParams.toString()}`;
        return fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                const apiResponse = new ApiQueryResponse(data);
                const results = apiResponse.getResultsArray(true) || [];
                const total = data.count || results.length || 0;
                return { results, total };
            });
    }

    /**
     * Fetch one page of research bids for export (paginated export-all API).
     * @deprecated Use fetchResearchBidsPage(page, limit, true) instead
     */
    private fetchResearchBidsExportPage(page: number, limit: number): Promise<any[]> {
        return this.fetchResearchBidsPage(page, limit, true).then(({ results }) => results);
    }

    /**
     * Fetch ALL research bids via delayed paginated requests (500 per page, like infinite scroll).
     * Accumulates results, then returns once complete.
     * @param useExportApi If true, use export-all API; otherwise use regular search API
     * @param pageSize Page size per request (batch size)
     * @param onProgress Optional callback to report progress: (current: number, total: number) => void
     * @returns Promise resolving to array of all results
     */
    private async fetchAllResearchBids(
        useExportApi: boolean = false, 
        pageSize: number = ResearchBids.EXPORT_PAGE_SIZE,
        onProgress?: (current: number, total: number) => void
    ): Promise<any[]> {
        const all: any[] = [];
        let page = 1;
        let batch: any[];
        let total = 0;
        do {
            const response = await this.fetchResearchBidsPage(page, pageSize, useExportApi);
            batch = response.results;
            all.push(...batch);
            
            // Update total from first response (use total from API if available)
            if (page === 1) {
                total = response.total > 0 ? response.total : 0;
                // Report initial progress with total (even if 0, to show "Initializing...")
                if (onProgress) {
                    onProgress(all.length, total);
                }
            } else {
                // Report progress for subsequent pages
                if (onProgress && total > 0) {
                    onProgress(all.length, total);
                }
            }
            
            page += 1;
        } while (batch.length === pageSize);
        
        // Final progress update
        if (onProgress && total > 0) {
            onProgress(all.length, total);
        }
        
        return all;
    }

    /**
     * Fetch next batch for infinite scroll (progressive loading).
     * @param page Page number to fetch (1-based)
     * @param useExportApi If true, use export-all API; otherwise use regular search API
     * @returns Promise resolving to { results: any[], total: number, hasMore: boolean }
     */
    private async fetchInfiniteScrollBatch(page: number, useExportApi: boolean = false): Promise<{ results: any[], total: number, hasMore: boolean }> {
        const pageSize = ResearchBids.GRID_ALL_PAGE_SIZE;
        const response = await this.fetchResearchBidsPage(page, pageSize, useExportApi);
        const hasMore = response.results.length === pageSize;
        return {
            results: response.results,
            total: response.total,
            hasMore,
        };
    }

    /**
     * Start infinite scroll loading - loads first batch and sets up for progressive loading.
     */
    private startInfiniteScroll() {
        this.setState({
            infiniteScrollData: [],
            infiniteScrollLoading: true,
            infiniteScrollCurrentPage: 0,
            infiniteScrollHasMore: true,
            infiniteScrollTotal: 0,
        });

        // Show toast notification
        this.showNotification('Loading all results...', 'infiniteScroll');

        // Load first batch
        this.loadNextInfiniteScrollBatch();
    }

    /**
     * Load next batch for infinite scroll and append to grid.
     */
    private loadNextInfiniteScrollBatch = async () => {
        if (this.state.infiniteScrollLoading && this.state.infiniteScrollCurrentPage > 0) {
            // Already loading, don't trigger again
            return;
        }

        if (!this.state.infiniteScrollHasMore && this.state.infiniteScrollCurrentPage > 0) {
            // No more data to load
            return;
        }

        this.setState({ infiniteScrollLoading: true });

        try {
            const nextPage = this.state.infiniteScrollCurrentPage + 1;
            const { results, total, hasMore } = await this.fetchInfiniteScrollBatch(nextPage, false);

            // Extend grid data
            const extendedResults = this.extendGridData(results);

            // Accumulate data
            const newData = [...this.state.infiniteScrollData, ...extendedResults];

            this.setState({
                infiniteScrollData: newData,
                infiniteScrollTotal: total || newData.length,
                infiniteScrollCurrentPage: nextPage,
                infiniteScrollHasMore: hasMore,
                infiniteScrollLoading: false,
            });

            // Update grid data source with accumulated data
            // For infinite scroll, we need to update the data source and refresh the grid
            if (this.gridWidget && this.gridDataSource) {
                // Update the data source's data array
                this.gridDataSource.data(newData);
                this.gridDataSource.total(newData.length);
                
                // Force grid to refresh and show new data
                this.gridWidget.refresh();
            }
            
            // Also trigger a re-render by updating state (which will cause grid to refresh)
            this.forceUpdate();
            
            // If there's more data, automatically load next batch after a short delay
            // This creates the infinite scroll effect
            if (hasMore) {
                // Small delay to allow UI to update and show the spinner
                setTimeout(() => {
                    this.loadNextInfiniteScrollBatch();
                }, 300);
            }
        } catch (error) {
            console.error('Error loading infinite scroll batch:', error);
            this.setState({
                infiniteScrollLoading: false,
                infiniteScrollHasMore: false,
            });
        }
    }

    /**
     * Render bottom loading indicator for infinite scroll (text only, no spinner to avoid overlap).
     */
    private renderInfiniteScrollSpinner() {
        if (!this.state.infiniteScrollLoading || this.state.infiniteScrollData.length === 0) {
            return null;
        }

        return (
            <Row>
                <Col xs={12} style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                        Loading more results... ({this.state.infiniteScrollData.length} loaded)
                    </div>
                </Col>
            </Row>
        );
    }

    /**
     * Show toast notification (using Bootstrap Alert styled to match app).
     */
    private showNotification(message: string, type: 'infiniteScroll' | 'export') {
        if (type === 'infiniteScroll') {
            this.setState({ showInfiniteScrollToast: true });
            // Auto-dismiss after 3 seconds for infinite scroll (short notification)
            setTimeout(() => {
                this.setState({ showInfiniteScrollToast: false });
            }, 3000);
        } else if (type === 'export') {
            // For export, show toast and keep it visible during the entire export process
            // It will be dismissed when export completes (handled in performExportKML)
            this.setState({ showExportToast: true });
        }
    }

    /**
     * Render toast notifications using Bootstrap Alert (styled to match app).
     */
    private renderToasts() {
        return (
            <div style={{ 
                position: 'fixed', 
                top: '20px', 
                right: '20px', 
                zIndex: 10000, 
                maxWidth: '400px',
                minWidth: '300px'
            }}>
                {this.state.showInfiniteScrollToast && (
                    <Alert 
                        bsStyle="info" 
                        onDismiss={() => this.setState({ showInfiniteScrollToast: false })}
                        className="toast-slide-in"
                        style={{ marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    >
                        <strong>Loading all results...</strong>
                        <br />
                        <small>Fetching data in batches. This may take a moment.</small>
                    </Alert>
                )}
                {this.state.showExportToast && (
                    <Alert 
                        bsStyle="info" 
                        onDismiss={this.state.exportingKML ? undefined : () => this.setState({ showExportToast: false })}
                        className="toast-slide-in"
                        style={{ marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    >
                        <strong>Exporting to Google Earth...</strong>
                        <br />
                        {this.state.exportProgress.total > 0 ? (
                            <small>
                                {this.state.exportProgress.current} of {this.state.exportProgress.total} research bids processed
                            </small>
                        ) : (
                            <small>Initializing...</small>
                        )}
                    </Alert>
                )}
            </div>
        );
    }

    /**
     * Fetch ALL research bids for Google Earth export via delayed paginated requests
     * (500 per page, like infinite scroll). Accumulates results, then returns once complete.
     */
    private async fetchAllResearchBidsForExport(): Promise<any[]> {
        return this.fetchAllResearchBids(
            true, 
            ResearchBids.EXPORT_PAGE_SIZE,
            (current, total) => {
                // Update export progress in state
                this.setState({
                    exportProgress: {
                        current,
                        total,
                    },
                });
            }
        );
    }

    private createGridDataSource() {
        const apiUrl = getApiUrl();

        // Use arrow function to preserve 'this' context and always read latest props/state
        const readFunction = (options: any) => {
            // Don't call API until search button is clicked
            if (!this.state.searchTriggered) {
                options.success({
                    data: [],
                    total: 0,
                });
                return;
            }

            const accessToken = ReduxQueries.PropsAccessToken(this.props);
            const filters = this.props.search.filters;
            
            // Use appliedFilters (cached when Search was clicked) for consistent pagination
            const applied = this.state.appliedFilters || {};

            const { page, pageSize } = options.data;
            const pageNumber = page || 1;

            // Kendo Grid may pass:
            // - 'all' (string) when selecting the "all" page size option
            // - a numeric value equal to total results (ex: 8000) in some configurations
            // We must treat any non-standard numeric pageSize as "all" and batch fetch with limit=500.
            const pageSizeStr = String(pageSize).toLowerCase();
            const numericPageSize = (typeof pageSize === 'number')
                ? pageSize
                : parseInt(pageSizeStr, 10);

            const knownPageSizes = [
                5,
                GridUtilities.KendoGridDefaultPageSize,
                25,
                50,
                100,
            ];

            const isAllResults =
                pageSize === GridUtilities.KendoGridPageSizeAll ||
                pageSize === 'all' ||
                pageSizeStr === 'all' ||
                // If Kendo gives us a numeric pageSize that isn't one of our configured sizes,
                // treat it as "all" (prevents one massive backend request).
                (Number.isFinite(numericPageSize as any) &&
                    numericPageSize > 0 &&
                    !_.includes(knownPageSizes, numericPageSize));

            const limitNumber = (Number.isFinite(numericPageSize as any) && numericPageSize > 0)
                ? numericPageSize
                : GridUtilities.KendoGridDefaultPageSize;

            if (isAllResults) {
                // Infinite scroll mode - use accumulated data
                // If no data yet and not loading, start the infinite scroll process
                if (this.state.infiniteScrollData.length === 0 && !this.state.infiniteScrollLoading) {
                    // Start infinite scroll in background
                    setTimeout(() => {
                        this.startInfiniteScroll();
                    }, 0);
                }
                
                // Return current accumulated data (will be empty initially, then populated as batches load)
                options.success({
                    data: this.state.infiniteScrollData,
                    total: this.state.infiniteScrollTotal || this.state.infiniteScrollData.length,
                });
                return;
            }

            // Reset infinite scroll state when switching away from "All"
            if (this.state.infiniteScrollData.length > 0) {
                this.setState({
                    infiniteScrollData: [],
                    infiniteScrollLoading: false,
                    infiniteScrollTotal: 0,
                    infiniteScrollCurrentPage: 0,
                    infiniteScrollHasMore: false,
                });
            }

            // Regular paginated request
            this.fetchResearchBidsPage(pageNumber, limitNumber, false)
                .then(({ results, total }) => {
                    // Extend grid data
                    const extendedResults = this.extendGridData(results);

                    options.success({
                        data: extendedResults,
                        total: total,
                    });
                })
                .catch((error) => {
                    console.error('Error fetching research bids:', error);
                    options.error(error);
                });
        };

        // Destroy old DataSource if it exists
        if (this.gridDataSource && typeof this.gridDataSource.destroy === 'function') {
            this.gridDataSource.destroy();
        }

        // Always use server-side paging - the readFunction handles both normal pagination and infinite scroll
        this.gridDataSource = GridUtilities.kendoGridDataSourceServerPaged(
            readFunction,
            GridUtilities.KendoGridDefaultPageSize,
            {}
        );
    }

    private setGridRef = (grid: any) => {
        if (!grid) {
            this.gridWidget = null;
            return;
        }

        const widget = grid && grid.kendoWidget ? grid.kendoWidget() : null;
        this.gridWidget = widget;

        // Grid with server-side paging will automatically call read() when it mounts
        // No need to manually call read() here
        if (this.gridWidget && this.gridDataSource) {
            // Ensure dataSource is set (though it should already be set via props)
            this.gridWidget.setDataSource(this.gridDataSource);
        }
    };

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map(r => {
            let record = _.cloneDeep(r);

            record.associationName = _.get(record, 'association.name', '').trim();
            record.clientName = _.get(record, 'client.name', '').trim();
            record.regionName = _.get(record, 'client.region.name', '').trim();
            record.orderStatus = _.get(record, 'status.name', '');
            record.feedback = record.feedback ? this.appText('feedback-yes') : this.appText('feedback-no');

            record.level1Economy = OrderPricing.getOrderPricingLevel1Economy(record.pricingMatrix);
            record.level2Economy = OrderPricing.getOrderPricingLevel2Economy(record.pricingMatrix);
            record.level3Economy = OrderPricing.getOrderPricingLevel3Economy(record.pricingMatrix);
            record.clientAddress = Formatters.composeLocationStreetAddress(record.client.location);
            record.clientCity = Formatters.getStr(record.client.location, 'city', '');
            record.clientState = Formatters.getStr(record.client.location, 'stateCode', '');
            record.clientPostal = Formatters.getStr(record.client.location, 'postalCode', '');
            // Order Dates
            record.bidDateValue = record.bidDate ? new Date(record.bidDate) : '';
            record.completionDateValue = record.completionDate ? new Date(record.completionDate) : '';
            record.siDueDate = record.siDueDate ? new Date(record.siDueDate) : '';
            record.preparedForFYEDate = record.preparedForFYEDate ? Formatters.formatDateShortDate(record.preparedForFYEDate) : '';
            // Extra columns
            record.acceptPrice = Formatters.getFloat(record, 'acceptedPrice');
            record.associationCity = Formatters.getStr(record, 'association.location.city');
            record.associationMailingAddress = Formatters.composeLocationFullAddress(record.association.location);
            record.associationState = Formatters.getStr(record, 'association.location.stateCode');

            // Association Location Details for Map Export
            record.associationLocationMapAddress1 = Formatters.getStr(record, 'association.location.address1');
            record.associationLocationMapCity = Formatters.getStr(record, 'association.location.city');
            record.associationLocationMapState = Formatters.getStr(record, 'association.location.stateCode');
            record.associationLocationMapZip = Formatters.getStr(record, 'association.location.postalCode');
            record.associationType = Formatters.getStr(record, 'association.associationType.name');
            record.numberBuildings = Formatters.getInt(record, 'association.details.numberBuildings');
            record.recRooms = Formatters.getStr(record, 'association.details.recRooms');
            record.constructDate = Formatters.getStr(record, 'association.details.constructDate');
            record.contactName = Formatters.composeClientContactFormal(record.contact);
            record.contactEmail = Formatters.getStr(record.contact, 'email');
            record.elevators = Formatters.getStr(record, 'association.details.elevators');
            record.fencing = Formatters.getStr(record, 'association.details.fencing');
            record.gated = Formatters.getStr(record, 'association.details.secured');
            record.interiorHalls = Formatters.getStr(record, 'association.details.interiorHalls');
            record.irrigation = Formatters.getStr(record, 'association.details.irrigation');
            record.mechanical = Formatters.getStr(record, 'association.details.mechanical');
            record.siding = Formatters.getStr(record, 'association.details.siding');
            record.pools = Formatters.getInt(record, 'association.details.pools');
            record.roofs = Formatters.getStr(record, 'association.details.roofs');
            record.service = Formatters.getStr(record, 'serviceLevel.name');
            record.spas = Formatters.getInt(record, 'association.details.spas');
            record.numberStories = Formatters.getInt(record, 'association.details.numberStories');
            record.streets = Formatters.getStr(record, 'association.details.streets');
            record.tennis = Formatters.getInt(record, 'association.details.tennis');
            record.numberUnits = Formatters.getInt(record, 'association.details.numberUnits');
            record.managerUser = Formatters.composeUsernameLabel(record.user, true);
            record.coordinatorUser = Formatters.composeUsernameLabel(record.siteInspector, true);
            record.serviceLevel = Formatters.getStr(record, 'serviceLevel.name', 'N/A');

            // View order detail link
            record.viewOrderDetailLink = '';
            record.viewOrderDetailLinkHref = '';
            if (record.orderId) {
                record.viewOrderDetailLink = this.appText('view-order-detail-link-title');
                // record.viewOrderDetailLinkHref = NavigationLinks.orderEdit(record.orderId);
                record.viewOrderDetailLinkHref = NavigationLinks.orderDetail(record.orderId);
            }

            return record;
        });
    }

    public filterSelectValuesAnyOption(values: any, anyOptionValue: any) {
        // Adds or removes 'any' option value
        let valuesFiltered = _.cloneDeep(values);

        _.remove(valuesFiltered, (o) => String(o) === String(anyOptionValue));

        if (!valuesFiltered || _.size(valuesFiltered) <= 0) {
            valuesFiltered = _.stubArray();
            valuesFiltered.push(anyOptionValue);
        }

        const result = _.cloneDeep(valuesFiltered);

        return result;
    }

    public handleSelectStreet(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueTextValue);
        this.setState({ streetsMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterStreetsMulti(valuesFiltered));
    }

    public handleSelectAssociationType(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueStatusId);
        this.setState({ associationTypeIdsMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterAssociationTypesMulti(valuesFiltered));
    }

    public handleSelectClient(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueStatusId);
        this.setState({ clientIdsMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterClientsMulti(valuesFiltered));
    }

    public handleSelectRegion(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueStatusId);
        this.setState({ regionIdsMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterRegionsMulti(valuesFiltered));
    }

    public handleSelectStatus(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueStatusId);
        this.setState({ statusIdsMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterStatusesMulti(valuesFiltered));
    }

    public handleSelectPool(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueStatusId);
        this.setState({ poolsMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterPoolsMulti(valuesFiltered));
    }

    public handleSelectSpa(e: any) {
        const values = _.map(e, (o) => _.get(o, 'value', ''));

        const valuesFiltered = this.filterSelectValuesAnyOption(values, this.anyOptionValueStatusId);
        this.setState({ spasMulti: valuesFiltered });
        // this.props.dispatch(Actions.searchResearchBidsFilterSpasMulti(valuesFiltered));
    }

    public handleSelectBidSentRange(e: any) {
        const value = _.get(e, 'value', '');
        this.setState({ dateRange: value });
        this.setState({ fromBidDate: DateRanges.getStartDate(value) });
        this.setState({ toBidDate: DateRanges.getEndDate(value) });
        // this.props.dispatch(Actions.searchResearchBidsDateRange(value));
    }

    public handleSelectToBidDate(e: any) {
        const value = e ? e.format(AppConfiguration.getMomentShortDateFormat()) : '';
        this.setState({ toBidDate: value });
        // this.props.dispatch(Actions.searchResearchBidsFilterToBidDate(value));
    }

    public handleSelectFromCompletionDate(e: any) {
        const value = e ? e.format(AppConfiguration.getMomentShortDateFormat()) : '';
        this.setState({ fromCompletionDate: value });
        // this.props.dispatch(Actions.searchResearchBidsFilterFromCompletionDate(value));
    }

    public handleSelectToCompletionDate(e: any) {
        const value = e ? e.format(AppConfiguration.getMomentShortDateFormat()) : '';
        this.setState({ toCompletionDate: value });
        // this.props.dispatch(Actions.searchResearchBidsFilterToCompletionDate(value));
    }

    public handleFilterUnitsRangeMin(e: any) {
        this.setState({ unitsMin: _.get(e, 'value', 0) }, () => {
            this.debouncedRangeSearch(this.state.unitsMin, this.state.unitsMax, Actions.searchResearchBidsFilterUnitsRange);
        });
    }

    public handleFilterUnitsRangeMax(e: any) {
        this.setState({ unitsMax: _.get(e, 'value', 0) }, () => {
            // this.debouncedRangeSearch(this.state.unitsMin, this.state.unitsMax, Actions.searchResearchBidsFilterUnitsRange);
        });
    }

    public handleFilterBuildingsRangeMin(e: any) {
        this.setState({ buildingsMin: _.get(e, 'value', 0) }, () => {
            // this.debouncedRangeSearch(this.state.buildingsMin, this.state.buildingsMax, Actions.searchResearchBidsFilterBuildingsRange);
        });
    }

    public handleFilterBuildingsRangeMax(e: any) {
        this.setState({ buildingsMax: _.get(e, 'value', 0) }, () => {
            // this.debouncedRangeSearch(this.state.buildingsMin, this.state.buildingsMax, Actions.searchResearchBidsFilterBuildingsRange);
        });
    }

    public handleSearchFilter(e: any) {
        const contactKeyword = e.target.value;
        this.setState({ contactKeyword: contactKeyword });
        // this.props.dispatch(Actions.searchAssociationsKeyword(keyword));
    }

    public handleExportKML(e: any) {
        if (!this.state.searchTriggered) {
            return;
        }
        if (this.state.exportingKML) {
            return;
        }
        // Show confirmation modal first
        this.setState({ showModalConfirmExport: true });
    }

    public handleCancelExportModal = () => {
        this.setState({ showModalConfirmExport: false });
    }

    public handleConfirmExportKML = () => {
        this.setState({ showModalConfirmExport: false });
        this.performExportKML();
    }

    private performExportKML() {
        const token = _.get(this.props, 'authsession.access_token', '');
        // Show toast notification immediately and reset progress
        this.setState({ 
            exportingKML: true,
            showExportToast: true, // Show toast immediately
            exportProgress: {
                current: 0,
                total: 0,
            },
        });

        // Use separate export-all API to fetch ALL research bids (not paginated)
        this.fetchAllResearchBidsForExport()
            .then((results) => {
                const extendedResults = this.extendGridData(results || []);
                const changeData = _.merge(
                    {},
                    {
                        orders: _.map(extendedResults, (o) => {
                            const placemarkAddress = `${_.get(o, 'associationLocationMapAddress1', '')} ${_.get(o, 'associationLocationMapCity', '')}, ${_.get(o, 'associationLocationMapState', '')} ${_.get(o, 'associationLocationMapZip', '')}`.trim();
                            const placemarkDescription = `Order #${_.get(o, 'orderId', '')}`;
                            return {
                                placemarkAddress,
                                placemarkDescription,
                                name: Formatters.getStr(o, 'associationName'),
                                address: Formatters.getStr(o, 'associationLocationMapAddress1'),
                                city: Formatters.getStr(o, 'associationLocationMapCity'),
                                stateCode: Formatters.getStr(o, 'associationLocationMapState'),
                                postalCode: Formatters.getStr(o, 'associationLocationMapZip'),
                                associationId: Formatters.getInt(o, 'associationId'),
                                orderId: Formatters.getInt(o, 'orderId'),
                            };
                        })
                    },
                    {
                        kmlConfig: {
                            folderName: 'Research Bids',
                        }
                    }
                );
                return this.props.downloadKML(changeData, token, false).then((response: any) => {
                    if (response.status >= 200 && response.status < 300) {
                        const kmlFilename = `research-bids-${moment().format('YYYY-MM-DD_HH-mm-ss')}.kml`;
                        const kmlData = Formatters.getStr(response, 'text');
                        fileDownload(kmlData, kmlFilename);
                    } else {
                        console.error('Error exporting KML:', response);
                    }
                });
            })
            .catch((err) => {
                console.error('Error fetching research bids for Google export:', err);
            })
            .then(() => {
                // Show 100% completion briefly before hiding
                const finalProgress = this.state.exportProgress;
                if (finalProgress.total > 0 && finalProgress.current < finalProgress.total) {
                    // Update to 100% if not already there
                    this.setState({
                        exportProgress: {
                            current: finalProgress.total,
                            total: finalProgress.total,
                        },
                    });
                }
                
                // Hide toast and reset state after a short delay to show completion
                setTimeout(() => {
                    this.setState({ 
                        exportingKML: false,
                        showExportToast: false,
                        exportProgress: {
                            current: 0,
                            total: 0,
                        },
                    });
                }, 1500);
            });
    }

    public renderModalConfirmExport() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalConfirmExport} onHide={this.handleCancelExportModal}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>Export to Google Earth</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        This export function may be slow depending on the size of your search results. 
                        The system will fetch all matching research bids in batches before generating the export file.
                    </p>
                    <p>
                        Do you want to continue?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={this.handleConfirmExportKML}>Continue Export</Button>
                    <Button onClick={this.handleCancelExportModal}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public handleSearchButtonClick = () => {
        const filters = this.props.search.filters;
        
        // Cache current filter values - these will be used for all API calls including pagination
        const appliedFilters = {
            buildingsMax: this.state.buildingsMax || filters.buildingsHigh,
            buildingsMin: this.state.buildingsMin || filters.buildingsLow,
            unitsMax: this.state.unitsMax || filters.unitsHigh,
            unitsMin: this.state.unitsMin || filters.unitsLow,
            contactKeyword: this.state.contactKeyword || filters.contactKeyword || '',
            fromBidDate: this.state.fromBidDate || filters.fromBidDate || '',
            toBidDate: this.state.toBidDate || filters.toBidDate || '',
            fromCompletionDate: this.state.fromCompletionDate || filters.fromCompletionDate || '',
            toCompletionDate: this.state.toCompletionDate || filters.toCompletionDate || '',
            associationTypeIdsMulti: this.state.associationTypeIdsMulti || filters.associationTypeIdsMulti || [-1],
            clientIdsMulti: this.state.clientIdsMulti || filters.clientIdsMulti || [-1],
            poolsMulti: this.state.poolsMulti || filters.poolsMulti || [-1],
            regionIdsMulti: this.state.regionIdsMulti || filters.regionIdsMulti || [-1],
            spasMulti: this.state.spasMulti || filters.spasMulti || [-1],
            statusIdsMulti: this.state.statusIdsMulti || filters.statusIdsMulti || [-1],
            streetsMulti: this.state.streetsMulti || filters.streetsMulti || [''],
        };

        // Set searchTriggered to true, increment searchVersion, and cache applied filters
        // searchVersion change triggers DataSource recreation in render() (via getFiltersHash)
        this.setState((prevState: any) => ({
            searchTriggered: true,
            searchVersion: prevState.searchVersion + 1,
            appliedFilters: appliedFilters,
        }));
    };

    public render() {
        // Server-side pagination: Create or recreate dataSource if filters changed
        const currentFiltersHash = this.getFiltersHash();
        if (!this.gridDataSource || this.lastFiltersHash !== currentFiltersHash) {
            this.lastFiltersHash = currentFiltersHash;
            // Destroy old dataSource if exists
            if (this.gridDataSource && typeof this.gridDataSource.destroy === 'function') {
                this.gridDataSource.destroy();
            }
            // Create new dataSource with current filters
            this.createGridDataSource();
        }

        const dataSource = this.gridDataSource;
        const gridKey = currentFiltersHash; // Use filters hash as key to force re-mount when filters change

        let buttonProps = {
            bsStyle: 'info',
            bsSize: 'sm',
            className: 'btn-search',
        };

        const { filterByAssociationTypesMulti, filterByClientsMulti, filterByRegionsMulti, filterByStreetTypesMulti, filterByStatuses, filterByStatusesMulti, ...rest } = this.props;
        const textAnyOption = this.anyOptionText;

        // Prepare select dropdown options
        const associationTypes = FormUtilities.getExtendedFilterOptionsMulti(filterByAssociationTypesMulti, 'associationTypeId', 'label', textAnyOption, -1);
        const clients = FormUtilities.getExtendedFilterOptionsMulti(filterByClientsMulti, 'clientId', 'label', textAnyOption, -1);
        const regions = FormUtilities.getExtendedFilterOptionsMulti(filterByRegionsMulti, 'regionId', 'label', textAnyOption, -1);
        const streets = FormUtilities.getExtendedFilterOptionsMulti(filterByStreetTypesMulti, 'name', 'label', textAnyOption, '');
        const statuses = FormUtilities.getExtendedFilterOptionsMulti(filterByStatusesMulti, 'statusId', 'statusId', textAnyOption, -1);

        const filterByPools = _.cloneDeep(_.range(this.minPoolSpaValue, this.maxPoolSpaValue).map((c) => ({ value: c, name: c })));
        const pools = FormUtilities.getExtendedFilterOptionsMulti(filterByPools, 'value', 'value', textAnyOption, -1);

        const filterBySpas = _.cloneDeep(_.range(this.minPoolSpaValue, this.maxPoolSpaValue).map((c) => ({ value: c, name: c })));
        const spas = FormUtilities.getExtendedFilterOptionsMulti(filterBySpas, 'value', 'value', textAnyOption, -1);

        const searchFilters = _.get(this.props, 'search.filters');

        const buildingsFilterLabel = this.appText('filter-by-buildings-range')
            .replace('{LOW}', searchFilters.buildingsLow)
            .replace('{HIGH}', searchFilters.buildingsHigh);

        const unitsFilterLabel = this.appText('filter-by-units-range');

        const pageTitle = this.appTextPageTitle();

        const bidRangeOptionsExtended = [
            { value: DateRanges.DateRangeOptionValues.ALL_DATES, label: this.appText('filter-date-range-options.all-dates') },
            { value: DateRanges.DateRangeOptionValues.LAST_THIRTY_DAYS, label: this.appText('filter-date-range-options.last-thirty-days') },
            { value: DateRanges.DateRangeOptionValues.MONTH_TO_DATE, label: this.appText('filter-date-range-options.month-to-date') },
            { value: DateRanges.DateRangeOptionValues.YEAR_TO_DATE, label: this.appText('filter-date-range-options.year-to-date') },
            { value: DateRanges.DateRangeOptionValues.PREVIOUS_MONTH, label: this.appText('filter-date-range-options.previous-month') },
            { value: DateRanges.DateRangeOptionValues.PREVIOUS_YEAR, label: this.appText('filter-date-range-options.previous-year') },
            { value: DateRanges.DateRangeOptionValues.LAST_THREE_MONTHS, label: this.appText('filter-date-range-options.last-three-months') },
            { value: DateRanges.DateRangeOptionValues.LAST_SIX_MONTHS, label: this.appText('filter-date-range-options.last-six-months') },
            { value: DateRanges.DateRangeOptionValues.LAST_TWELVE_MONTHS, label: this.appText('filter-date-range-options.last-twelve-months') },
            { value: DateRanges.DateRangeOptionValues.LAST_TWO_YEARS, label: this.appText('filter-date-range-options.last-two-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_THREE_YEARS, label: this.appText('filter-date-range-options.last-three-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_FOUR_YEARS, label: this.appText('filter-date-range-options.last-four-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_FIVE_YEARS, label: this.appText('filter-date-range-options.last-five-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_SIX_YEARS, label: this.appText('filter-date-range-options.last-six-years') },
        ];
        let bidRangeOptions = AppUtilities.isArrayWithLength(bidRangeOptionsExtended) ? FormUtilities.buildSelectOptions(bidRangeOptionsExtended, '', 'label', 'value') : [];

        const page = (
            <WhiteBoxPageSection title={pageTitle} >
                <Row>
                    <Col xs={12}>
                        <div className="research-bids-form">
                            <form onSubmit={e => { e.preventDefault(); }}>
                                <Row className="form-group">
                                    <Col xs={3} xl={2}>
                                        <div>
                                            <label className="control-label">{this.appText('filter-by-bid-sent-range')}</label>
                                            <VirtualizedSelect
                                                clearable={false}
                                                name="bid-sent-range-filter"
                                                onChange={e => this.handleSelectBidSentRange(e)}
                                                options={bidRangeOptions}
                                                value={(this.state.dateRange) ? this.state.dateRange : searchFilters.dateRange}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={3} xl={2}>
                                        <label className="search-button-label">{this.appText('search')}</label>
                                        <ButtonToolbar>
                                            <Button {...buttonProps} onClick={this.handleSearchButtonClick}>{this.appText('search')}</Button>
                                        </ButtonToolbar>
                                    </Col>
                                </Row>

                                <Row className="form-group">
                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-status')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={statuses.filter}
                                            name="status-filter"
                                            onChange={e => this.handleSelectStatus(e)}
                                            options={statuses.options}
                                            value={(this.state.statusIdsMulti) ? this.state.statusIdsMulti : searchFilters.statusIdsMulti}
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-streets')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={streets.filter}
                                            name="street-filter"
                                            onChange={e => this.handleSelectStreet(e)}
                                            options={streets.options}
                                            value={(this.state.streetsMulti) ? this.state.streetsMulti : searchFilters.streetsMulti}
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-region')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={regions.filter}
                                            name="region-filter"
                                            onChange={e => this.handleSelectRegion(e)}
                                            options={regions.options}
                                            value={(this.state.regionIdsMulti) ? this.state.regionIdsMulti : searchFilters.regionIdsMulti}
                                        />
                                    </Col>
                                </Row>

                                <Row className="form-group">
                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{buildingsFilterLabel}</label>
                                        <KendoWrappers.KendoNumberInput
                                            name="rangeConfigBuildingsMin"
                                            label={'Min buildings: '}
                                            value={this.state.buildingsMin}
                                            spinners={false}
                                            min={0}
                                            max={100000}
                                            onChange={e => this.handleFilterBuildingsRangeMin(e)}
                                            width={'auto'}
                                            format="###"
                                        />
                                        <KendoWrappers.KendoNumberInput
                                            name="rangeConfigBuildingsMax"
                                            label={'Max buildings: '}
                                            value={this.state.buildingsMax}
                                            spinners={false}
                                            min={0}
                                            max={100000}
                                            onChange={e => this.handleFilterBuildingsRangeMax(e)}
                                            width={'auto'}
                                            format="###"
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-spas')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={spas.filter}
                                            name="spas-filter"
                                            onChange={e => this.handleSelectSpa(e)}
                                            options={spas.options}
                                            value={(this.state.spasMulti) ? this.state.spasMulti : searchFilters.spasMulti}
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-association-type')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={associationTypes.filter}
                                            name="association-type-filter"
                                            onChange={e => this.handleSelectAssociationType(e)}
                                            options={associationTypes.options}
                                            value={(this.state.associationTypeIdsMulti) ? this.state.associationTypeIdsMulti : searchFilters.associationTypeIdsMulti}
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-client')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={clients.filter}
                                            name="client-filter"
                                            onChange={e => this.handleSelectClient(e)}
                                            options={clients.options}
                                            value={(this.state.clientIdsMulti) ? this.state.clientIdsMulti : searchFilters.clientIdsMulti}
                                        />
                                    </Col>
                                </Row>

                                <Row className="form-group">
                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{unitsFilterLabel}</label>
                                        <KendoWrappers.KendoNumberInput
                                            name="rangeConfigUnitsMin"
                                            label={'Min units:'}
                                            value={(this.state.unitsMin) ? this.state.unitsMin : this.state.unitsMin}
                                            spinners={false}
                                            min={0}
                                            max={100000}
                                            onChange={e => this.handleFilterUnitsRangeMin(e)}
                                            width={'auto'}
                                            format="###"
                                        />
                                        <KendoWrappers.KendoNumberInput
                                            name="rangeConfigUnitsMax"
                                            label={'Max units:'}
                                            value={(this.state.unitsMax) ? this.state.unitsMax : this.state.unitsMax}
                                            spinners={false}
                                            min={0}
                                            max={100000}
                                            onChange={e => this.handleFilterUnitsRangeMax(e)}
                                            width={'auto'}
                                            format="###"
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('filter-by-pools')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            multi={true}
                                            filterOptions={pools.filter}
                                            name="pools-filter"
                                            onChange={e => this.handleSelectPool(e)}
                                            options={pools.options}
                                            value={(this.state.poolsMulti) ? this.state.poolsMulti : searchFilters.poolsMulti}
                                        />
                                    </Col>

                                    <Col xs={3} xl={2}>
                                        <label className="control-label">{this.appText('contact-keyword')}</label>
                                    <SearchInput
                                        debounceTimeout={1000}
                                        onChange={(e) => { this.handleSearchFilter(e); }}
                                        placeholder={this.appText('enter-keyword')}
                                        value={(this.state.contactKeyword) ? this.state.contactKeyword : searchFilters.contactKeyword}
                                    />
                                </Col>

                                    <Col xs={3} xl={2} />
                                </Row>
                            </form>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        {this.state.infiniteScrollLoading && this.state.infiniteScrollData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <div className="k-loading-image" />
                            </div>
                        ) : (
                            <Grid key={gridKey} ref={this.setGridRef} dataSource={dataSource} {...this.gridOptions} />
                        )}
                    </Col>
                </Row>
                {this.renderInfiniteScrollSpinner()}
            </WhiteBoxPageSection>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
                {this.renderModalConfirmExport()}
                {this.renderToasts()}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getResearchBidsSearchUrl();

const mapPropsToConfigs = (props) => {
    // Disable Redux Query - we're using server-side paging with Kendo Grid DataSource instead
    // Return null to prevent Redux Query from making API calls
    return null;
};

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByAssociationTypes: state.entities.researchBidsAssociationTypes,
        filterByClients: state.entities.researchBidsClients,
        filterByRegions: state.entities.researchBidsRegions,
        filterByStatuses: state.entities.researchBidsStatuses,
        filterByStatusesMulti: state.entities.researchBidsStatusesMulti,
        filterByStreetTypes: state.entities.researchBidsStreetTypes,
        filterByAssociationTypesMulti: state.entities.researchBidsAssociationTypesMulti,
        filterByClientsMulti: state.entities.researchBidsClientsMulti,
        filterByRegionsMulti: state.entities.researchBidsRegionsMulti,
        filterByStreetTypesMulti: state.entities.researchBidsStreetTypesMulti,
        search: {
            results: state.entities.researchBidsResults,
            filters: {
                associationTypeId: state.searchFiltersResearchBids.associationTypeId,
                buildingsHigh: state.searchFiltersResearchBids.buildingsHigh,
                buildingsLow: state.searchFiltersResearchBids.buildingsLow,
                clientId: state.searchFiltersResearchBids.clientId,
                contactKeyword: state.searchFiltersResearchBids.contactKeyword,
                dateRange: state.searchFiltersResearchBids.dateRange,
                fromBidDate: state.searchFiltersResearchBids.fromBidDate,
                toBidDate: state.searchFiltersResearchBids.toBidDate,
                fromCompletionDate: state.searchFiltersResearchBids.fromCompletionDate,
                toCompletionDate: state.searchFiltersResearchBids.toCompletionDate,
                operator: 'AND',
                pools: state.searchFiltersResearchBids.pools,
                regionId: state.searchFiltersResearchBids.regionId,
                spas: state.searchFiltersResearchBids.spas,
                statusId: state.searchFiltersResearchBids.statusId,
                streets: state.searchFiltersResearchBids.streets,
                unitsHigh: state.searchFiltersResearchBids.unitsHigh,
                unitsLow: state.searchFiltersResearchBids.unitsLow,

                associationTypeIdsMulti: state.searchFiltersResearchBids.associationTypeIdsMulti,
                clientIdsMulti: state.searchFiltersResearchBids.clientIdsMulti,
                poolsMulti: state.searchFiltersResearchBids.poolsMulti,
                regionIdsMulti: state.searchFiltersResearchBids.regionIdsMulti,
                spasMulti: state.searchFiltersResearchBids.spasMulti,
                statusIdsMulti: state.searchFiltersResearchBids.statusIdsMulti,
                streetsMulti: state.searchFiltersResearchBids.streetsMulti,
            }
        },
    });
};

const mapDispatchToProps = (dispatch) => ({
    dispatch: (d) => dispatch(d),
    downloadKML: (d: any, a: string, o: boolean) => dispatch(MutateActions.downloadKMLMutateAsync(d, a, o)),
});

const ResearchBidsContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(ResearchBids);

export default withRouter(ResearchBidsContainer);



// WEBPACK FOOTER //
// ./src/research-bids/index.tsx